// modules/ChatSystem.js
// 聊天系统模块 - 处理多人游戏聊天功能

import DebugHelper from './DebugHelper.js';
import MessageHelper from './MessageHelper.js';
import BackgroundHelper from './BackgroundHelper.js';
import UIStyleHelper from './UIStyleHelper.js';

export default class ChatSystem {
    constructor(scene, networkManager) {
        this.scene = scene;
        this.networkManager = networkManager;
        this.chatVisible = false;
        this.chatMessages = []; // 存储聊天消息
        this.maxMessages = 50; // 最大消息数
        this.elements = {
            panel: null,
            input: null,
            messages: [],
            toggleButton: null
        };
    }
    
    /**
     * 初始化聊天系统
     */
    init() {
        if (!this.networkManager) {
            DebugHelper.warn('ChatSystem: 网络管理器不存在');
            return;
        }
        
        // 注册消息事件
        this.networkManager.on('message', (payload) => {
            this.addMessage(payload.playerName, payload.message, false);
        });
        
        // 创建聊天UI
        this.createChatUI();
    }
    
    /**
     * 创建聊天UI
     */
    createChatUI() {
        const { width, height } = this.scene.cameras.main;
        
        // 聊天面板背景（使用BackgroundHelper）
        this.elements.panel = BackgroundHelper.createPanel(
            this.scene,
            10, height - 200, 400, 190,
            {
                fillColor: 0x000000,
                fillAlpha: 0.7,
                borderColor: 0x4cc9f0,
                borderWidth: 2
            }
        );
        this.elements.panel.setScrollFactor(0);
        this.elements.panel.setDepth(1000);
        this.elements.panel.setVisible(false);
        
        // 消息显示区域
        this.elements.messagesContainer = this.scene.add.container(20, height - 190);
        this.elements.messagesContainer.setScrollFactor(0);
        this.elements.messagesContainer.setDepth(1001);
        this.elements.messagesContainer.setVisible(false);
        
        // 输入框背景（使用BackgroundHelper）
        this.elements.inputBg = BackgroundHelper.createPanel(
            this.scene,
            20, height - 30, 380, 25,
            {
                fillColor: 0x2a2a3e,
                fillAlpha: 1,
                borderColor: 0x4cc9f0,
                borderWidth: 1
            }
        );
        this.elements.inputBg.setScrollFactor(0);
        this.elements.inputBg.setDepth(1001);
        this.elements.inputBg.setVisible(false);
        
        // 输入提示文本（使用UIStyleHelper）
        this.elements.inputHint = UIStyleHelper.createText(
            this.scene,
            '按 T 打开聊天，输入后空格发送',
            25, height - 25,
            { fontSize: '12px', fill: '#888888', fontFamily: 'monospace' }
        );
        this.elements.inputHint.setScrollFactor(0);
        this.elements.inputHint.setDepth(1002);
        this.elements.inputHint.setVisible(false);
        
        // 切换按钮
        this.elements.toggleButton = this.scene.add.text(
            width - 100, height - 30,
            '聊天 (T)',
            {
                fontSize: '14px',
                fill: '#4cc9f0',
                fontFamily: 'monospace',
                backgroundColor: '#000000',
                padding: { x: 10, y: 5 }
            }
        );
        this.elements.toggleButton.setOrigin(0.5);
        this.elements.toggleButton.setScrollFactor(0);
        this.elements.toggleButton.setDepth(1000);
        this.elements.toggleButton.setInteractive({ useHandCursor: true });
        this.elements.toggleButton.on('pointerdown', () => {
            this.toggleChat();
        });
        
        // 绑定键盘事件
        this.setupKeyboard();
    }
    
    /**
     * 设置键盘控制
     */
    setupKeyboard() {
        // T键切换聊天
        this.tKey = this.scene.input.keyboard.addKey(Phaser.Input.Keyboard.KeyCodes.T);
        this.tKey.on('down', () => {
            this.toggleChat();
        });
        
        // 空格键打开输入（聊天面板可见时）
        this.spaceKey = this.scene.input.keyboard.addKey(Phaser.Input.Keyboard.KeyCodes.SPACE);
        this.spaceKey.on('down', () => {
            if (this.chatVisible) {
                this.openInput();
            }
        });
    }
    
    /**
     * 切换聊天显示
     */
    toggleChat() {
        this.chatVisible = !this.chatVisible;
        
        const visible = this.chatVisible;
        if (this.elements.panel) this.elements.panel.setVisible(visible);
        if (this.elements.messagesContainer) this.elements.messagesContainer.setVisible(visible);
        if (this.elements.inputBg) this.elements.inputBg.setVisible(visible);
        if (this.elements.inputHint) this.elements.inputHint.setVisible(visible);
        
        if (visible) {
            this.updateMessagesDisplay();
        }
    }
    
    /**
     * 打开输入框
     */
    openInput() {
        const input = document.createElement('input');
        input.type = 'text';
        input.style.position = 'absolute';
        input.style.left = '25px';
        input.style.top = (this.scene.cameras.main.height - 25) + 'px';
        input.style.width = '370px';
        input.style.height = '20px';
        input.style.fontSize = '14px';
        input.style.fontFamily = 'monospace';
        input.style.backgroundColor = '#2a2a3e';
        input.style.color = '#ffffff';
        input.style.border = 'none';
        input.style.padding = '2px 5px';
        input.style.zIndex = '10000';
        document.body.appendChild(input);
        input.focus();
        
        const sendMessage = () => {
            const message = input.value.trim();
            if (message) {
                this.sendMessage(message);
            }
            document.body.removeChild(input);
        };
        
        input.addEventListener('keydown', (e) => {
            if (e.key === ' ' || e.code === 'Space') {
                e.preventDefault();
                sendMessage();
            } else if (e.key === 'Escape') {
                document.body.removeChild(input);
            }
        });
    }
    
    /**
     * 发送消息
     * @param {string} message - 消息内容
     */
    sendMessage(message) {
        if (!this.networkManager || !this.networkManager.isConnected()) {
            MessageHelper.showError(this.scene, '未连接到服务器');
            return;
        }
        
        if (!message || message.trim().length === 0) {
            return;
        }
        
        // 发送到服务器
        this.networkManager.sendMessage(message);
        
        // 本地显示
        const playerName = this.networkManager.playerName || '我';
        this.addMessage(playerName, message, true);
    }
    
    /**
     * 添加消息到聊天
     * @param {string} playerName - 玩家名称
     * @param {string} message - 消息内容
     * @param {boolean} isLocal - 是否本地消息
     */
    addMessage(playerName, message, isLocal = false) {
        const timestamp = new Date().toLocaleTimeString();
        const chatMessage = {
            playerName: playerName,
            message: message,
            timestamp: timestamp,
            isLocal: isLocal
        };
        
        this.chatMessages.push(chatMessage);
        
        // 限制消息数量
        if (this.chatMessages.length > this.maxMessages) {
            this.chatMessages.shift();
        }
        
        // 更新显示
        if (this.chatVisible) {
            this.updateMessagesDisplay();
        }
    }
    
    /**
     * 更新消息显示
     */
    updateMessagesDisplay() {
        if (!this.elements.messagesContainer) return;
        
        // 清除旧消息
        this.elements.messagesContainer.removeAll(true);
        
        // 显示最近的消息（最多10条）
        const recentMessages = this.chatMessages.slice(-10);
        const { height } = this.scene.cameras.main;
        
        recentMessages.forEach((msg, index) => {
            const color = msg.isLocal ? '#4cc9f0' : '#ffffff';
            const text = this.scene.add.text(
                0, index * 16,
                `[${msg.timestamp}] ${msg.playerName}: ${msg.message}`,
                {
                    fontSize: '12px',
                    fill: color,
                    fontFamily: 'monospace',
                    wordWrap: { width: 380 }
                }
            );
            text.setScrollFactor(0);
            this.elements.messagesContainer.add(text);
        });
    }
    
    /**
     * 清理
     */
    destroy() {
        if (this.elements.panel) this.elements.panel.destroy();
        if (this.elements.messagesContainer) this.elements.messagesContainer.destroy();
        if (this.elements.inputBg) this.elements.inputBg.destroy();
        if (this.elements.inputHint) this.elements.inputHint.destroy();
        if (this.elements.toggleButton) this.elements.toggleButton.destroy();
        
        this.chatMessages = [];
    }
}

